import { type ComponentProps } from 'react';
import { Card as HeroCard } from '@heroui/react';
import { cn } from '../utils';

type Padding = 'none' | 'sm' | 'md' | 'lg';
type CardRootProps = ComponentProps<typeof HeroCard> & {
  padding?: Padding;
};

type CardContentProps = ComponentProps<typeof HeroCard.Content> & {
  padding?: Padding;
};

const paddingClasses: Record<Padding, string> = {
  none: '',
  sm: 'p-4',
  md: 'p-5 sm:p-6',
  lg: 'p-6 sm:p-8'
};

const Root = ({ className, variant = 'default', padding = 'none', ...props }: CardRootProps) => (
  <HeroCard
    variant={variant}
    className={cn(
      'border border-default-200/80 bg-content1/95 shadow-[0_18px_45px_-28px_rgb(15_23_42_/_0.45)] dark:border-default-100/10',
      paddingClasses[padding],
      className
    )}
    {...props}
  />
);

const Header = ({ className, ...props }: ComponentProps<typeof HeroCard.Header>) => (
  <HeroCard.Header className={cn('gap-2', className)} {...props} />
);

const Content = ({ className, padding = 'none', ...props }: CardContentProps) => (
  <HeroCard.Content className={cn(paddingClasses[padding], className)} {...props} />
);

const Footer = ({ className, ...props }: ComponentProps<typeof HeroCard.Footer>) => (
  <HeroCard.Footer className={cn('gap-3 px-5 pb-5 sm:px-6 sm:pb-6', className)} {...props} />
);

export const Card = Object.assign(Root, {
  Header,
  Title: HeroCard.Title,
  Description: HeroCard.Description,
  Content,
  Footer
});
